// NIGHT-DIRECTSTAFF-2026-10-17 §5: app polish -- the icon badge, the delivery line under staff messages, pull-to-refresh.

/**
 * The installed app's icon badge = the Needs-a-human count (the Badging API). Supported by installed apps on Windows,
 * macOS and ChromeOS (Chrome / Edge), and on iOS 16.4+ home-screen apps once notifications are allowed. Android Chrome
 * has no app-icon badge API: the launcher shows a dot only while a notification is showing.
 * -> 'set' | 'cleared' | 'unsupported' | 'failed'
 */
export async function syncAppBadge(count, nav = typeof navigator !== 'undefined' ? navigator : null) {
  if (!nav || typeof nav.setAppBadge !== 'function') return 'unsupported';
  try {
    if (count > 0) { await nav.setAppBadge(count); return 'set'; }
    if (typeof nav.clearAppBadge === 'function') await nav.clearAppBadge(); else await nav.setAppBadge(0);
    return 'cleared';
  } catch { return 'failed'; }
}

// Twilio's codes in plain words (agents window, 10-17: error_code is a number column; error_message is Twilio's text or
// our stored Twilio title, digits masked -- the fallback for a code not listed here).
export const PLAIN_REASONS = {
  21211: 'not a valid phone number',
  21401: 'not a valid phone number',
  21408: "texting that country isn't switched on",
  21610: 'they replied STOP',
  21612: "that number can't get texts from us",
  21614: 'not a mobile number (a landline?)',
  30003: 'their phone is off or out of reach',
  30004: 'the number blocked our texts',
  30005: "the number doesn't exist at the carrier",
  30006: "a landline, or the carrier can't take texts",
  30007: 'the carrier filtered it as spam',
  30008: 'an unknown carrier error',
  30034: "our number isn't registered with the carriers yet",
};
export function plainReason(m) {
  const code = Number(m && m.error_code);
  if (code && PLAIN_REASONS[code]) return PLAIN_REASONS[code];
  const msg = String((m && m.error_message) || '').trim();
  if (msg) return msg.charAt(0).toLowerCase() + msg.slice(1);
  return code ? `carrier code ${code}` : 'no reason given';
}

const hhmm = (iso, now) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const n = new Date(now);
  const sameDay = d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
  return sameDay ? time : `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${time}`;
};

/**
 * The quiet line under a STAFF message -> { text, tone: 'ok' | 'good' | 'bad' | 'wait' }
 *   sent / no status yet   "Sent ✓ 18:39"
 *   delivered              "Sent ✓ 18:39 · Delivered"
 *   failed / undelivered   "Failed 18:39: their phone is off or out of reach"
 *   cancelled              "Not sent: not a mobile number (a landline?)"   (Twilio refused before sending)
 *   queued                 "Queued for their morning (quiet hours)"        sending   "Sending…"
 */
export function deliveryLine(m, now = Date.now()) {
  const s = String((m && m.status) || '').trim().toLowerCase();
  const at = hhmm(m && m.created_at, now);
  if (s === 'failed' || s === 'undelivered') return { text: `Failed${at ? ` ${at}` : ''}: ${plainReason(m)}`, tone: 'bad' };
  if (s === 'cancelled') return { text: `Not sent: ${plainReason(m)}`, tone: 'bad' };
  if (s === 'queued') return { text: 'Queued for their morning (quiet hours)', tone: 'wait' };
  if (s === 'sending') return { text: 'Sending…', tone: 'wait' };
  if (s === 'delivered') return { text: `Sent ✓${at ? ` ${at}` : ''} · Delivered`, tone: 'good' };
  return { text: `Sent ✓${at ? ` ${at}` : ''}`, tone: 'ok' };
}
export const TONE_COLOR = { ok: '#64748b', good: '#15803d', bad: '#b91c1c', wait: '#92400e' };

/**
 * Pull-to-refresh, the arithmetic: a touch that STARTS with the list at the top and pulls down. Damped so the
 * indicator moves at half the finger's speed. -> { dist, armed }  (armed = let go now and it refreshes)
 */
export const PULL_THRESHOLD = 70;
export function pullState(startY, y, atTop, threshold = PULL_THRESHOLD) {
  if (!atTop || startY == null) return { dist: 0, armed: false };
  const dist = Math.max(0, Math.min(120, (y - startY) / 2));
  return { dist, armed: dist >= threshold / 2 + 15 };
}
